

































































































































import { resetPassword, validateResetToken } from "@/data/repository/UserRepository";
import { Component, Vue, Watch } from "vue-property-decorator";
import { getBuildDate, getRunningBuild } from "@/utils/BuildInfo";
import {
  COLOR_LOGIN_BUTTON_DARK,
  COLOR_STANDARD_BLACK,
  COLOR_STANDARD_WHITE,
} from "@/utils/colors";

@Component({
  components: {

  },
})
export default class ResetPassword extends Vue {
  token: string | null = null;
  clientId: string | null = null;
  userId: string | null = null;

  newPassword = "";
  newPasswordRepeated = "";

  isLoading = false;
  isResettingPassword = false;
  resetPasswordError: string | null = null;
  resetPasswordSuccess: string | null = null;
  tokenInvalid = false;
  passwordResetCompleted = false;

  showPassword = false;
  showConfirmPassword = false;

  @Watch("email")
  onEmailChanged() {
    this.resetPasswordError = null;
  }

  @Watch("password")
  onPasswordChanged() {
    this.resetPasswordError = null;
  }

  get canSubmit() {
    return this.newPassword && this.newPasswordRepeated && this.newPassword === this.newPasswordRepeated && !this.isLoading;
  }

  // The theming below mirrors Login.vue, so the reset page follows dark mode like the login page.

  /** Focus colour of the fields (caret and underline) and the spinner, readable on either background. */
  get textFieldColor() {
    return this.$vuetify.theme.dark ? "white" : "black";
  }

  /** Text colour of a field the browser autofilled; Chrome forces it to black (see styles). */
  get autofillTextColor() {
    return this.$vuetify.theme.dark ? COLOR_STANDARD_WHITE : COLOR_STANDARD_BLACK;
  }

  /** Fill of the buttons: black on the light background, a soft off-white on the dark one. */
  get buttonColor() {
    return this.$vuetify.theme.dark ? COLOR_LOGIN_BUTTON_DARK : "black";
  }

  /** Label of the buttons, the opposite of their fill so it stays readable in both themes. */
  get buttonLabelClass() {
    return this.$vuetify.theme.dark ? "black--text" : "white--text";
  }

  get isDevelopmentWebsite() {
    return window.location.hostname.includes("development");
  }

  get versionString() {
    const dev = this.isDevelopmentWebsite;
    return this.$t(dev ? "common.development" : "common.production", [
      getRunningBuild().version,
    ]).toString();
  }

  get showDevDetails() {
    return !window.location.hostname.includes("localhost");
  }

  get devDetails() {
    const build = getRunningBuild();

    return (
      this.versionString +
      " / " +
      this.$t("common.build", [
        String(build.buildNumber),
        getBuildDate(build),
      ]).toString()
    );
  }

  goToLogin() {
    this.$router.push({ path: "/login" });
  }

  isNewPasswordValid() {
    const newPassword = this.newPassword;
    const newPasswordRepeated = this.newPasswordRepeated;

    if (newPassword.length < 8) {
      this.resetPasswordError = this.$t("resetPassword.errorMinPasswordLength").toString();
      return false;
    }

    if (newPassword != newPasswordRepeated) {
      this.resetPasswordError = this.$t("resetPassword.errorPasswordNoMatch").toString();
      return false;
    }

    return true;
  }

  toggleShowPassword() {
    this.showPassword = !this.showPassword;
  }

  toggleShowConfirmPassword() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  async created() {
    const route = this.$route;
    this.token = route.query.token as string || null;
    this.clientId = route.query.clientId as string || null;
    this.userId = route.query.userId as string || null;

    if (!this.token || !this.clientId || !this.userId) {
      this.tokenInvalid = true;
      return;
    }

    // Validate token
    try {
      this.isLoading = true;
      const isValid = await validateResetToken(this.clientId, this.userId, this.token);
      if (!isValid) {
        this.tokenInvalid = true;
      }
    } catch (error) {
      this.tokenInvalid = true;
    } finally {
      this.isLoading = false;
    }
  }

  async submitNewPassword() {
    this.resetPasswordError = null;
    this.resetPasswordSuccess = null;

    if (this.tokenInvalid || !this.canSubmit || !this.token || !this.clientId || !this.userId) {
      return;
    }

    if(!this.isNewPasswordValid()) {
      return;
    }

    this.isResettingPassword = true;

    try {
      const wasSuccessful = await resetPassword(this.clientId, this.userId, this.newPassword, this.token);
      if (wasSuccessful) {
        this.resetPasswordSuccess = this.$t("resetPassword.passwordResetSuccess").toString();
        this.passwordResetCompleted = true;
      } else {
        this.resetPasswordError = this.$t("resetPassword.passwordResetFailed").toString();
      }
    } catch (error) {
      this.resetPasswordError = this.$t("resetPassword.passwordResetFailed").toString();
    }

    this.isResettingPassword = false;
  }
}
